 import { useState } from "react";
 import { useNavigate } from "react-router-dom";
 import { Loader2, Mail, Check, Lock } from "lucide-react";
 import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
 import { Button } from "@/components/ui/button";
 import { Input } from "@/components/ui/input";
 import { Label } from "@/components/ui/label";
 import { useAuth } from "@/hooks/useAuth";
 import { supabase } from "@/integrations/supabase/client";
 import nexaboardLogo from "@/assets/nexaboard-logo-dark.png";
 
 interface LoginDialogProps {
   open: boolean;
   onOpenChange: (open: boolean) => void;
 }
 
 export const LoginDialog = ({ open, onOpenChange }: LoginDialogProps) => {
   const [email, setEmail] = useState("");
   const [password, setPassword] = useState("");
   const [isLoading, setIsLoading] = useState(false);
   const [isSignUp, setIsSignUp] = useState(false);
   const [isForgotPassword, setIsForgotPassword] = useState(false);
   const [resetEmailSent, setResetEmailSent] = useState(false);
   
   const { signIn, signUp } = useAuth();
   const navigate = useNavigate();
 
   const handleForgotPassword = async (e: React.FormEvent) => {
     e.preventDefault();
     setIsLoading(true);
 
     try {
       const { error } = await supabase.auth.resetPasswordForEmail(email, {
         redirectTo: `${window.location.origin}/reset-password`,
       });
 
       if (!error) {
         setResetEmailSent(true);
       }
     } catch (err) {
       // Silent fail
     } finally {
       setIsLoading(false);
     }
   };
 
   const handleSubmit = async (e: React.FormEvent) => {
     e.preventDefault();
     setIsLoading(true);
 
     try {
       if (isSignUp) {
         const { error } = await signUp(email, password);
         if (!error) {
           onOpenChange(false);
           navigate("/dashboard");
         }
       } else {
         const { error } = await signIn(email, password);
         if (!error) {
           onOpenChange(false);
           navigate("/dashboard");
         }
       }
     } catch (err) {
       // Silent fail
     } finally {
       setIsLoading(false);
     }
   };
 
   const resetState = () => {
     setIsForgotPassword(false);
     setResetEmailSent(false);
     setIsSignUp(false);
     setEmail("");
     setPassword("");
   };
 
   const handleOpenChange = (newOpen: boolean) => {
     if (!newOpen) {
       resetState();
     }
     onOpenChange(newOpen);
   };
 
   return (
     <Dialog open={open} onOpenChange={handleOpenChange}>
       <DialogContent className="dark bg-black/95 border-blue-500/30 backdrop-blur-xl sm:max-w-md">
         {/* Background gradient inside dialog */}
         <div className="absolute inset-0 -z-10 overflow-hidden rounded-lg">
           <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 h-[400px] w-[500px] rounded-full bg-gradient-to-br from-cyan-500/15 via-blue-500/15 to-purple-600/15 blur-[80px]" />
         </div>
 
         <DialogHeader className="text-center">
           {/* Logo */}
           <div className="flex items-center justify-center mb-4">
             <img src={nexaboardLogo} alt="NexaBoard Logo" className="h-16 w-auto" />
           </div>
           <DialogTitle className="sr-only">Login</DialogTitle>
         </DialogHeader>
 
         {isForgotPassword ? (
           resetEmailSent ? (
             <div className="text-center py-4">
               <div className="mx-auto w-16 h-16 bg-gradient-to-br from-cyan-500/20 via-blue-500/20 to-purple-600/20 rounded-full flex items-center justify-center mb-4 border border-cyan-500/30">
                 <Check className="h-8 w-8 text-cyan-400" />
               </div>
               <h2 className="text-xl font-bold mb-2 text-foreground">E-Mail gesendet</h2>
               <p className="text-muted-foreground mb-6 text-sm">
                 Wir haben Ihnen einen Link zum Zurücksetzen des Passworts an <span className="font-medium text-cyan-400">{email}</span> gesendet.
               </p>
               <Button 
                 variant="outline" 
                 className="w-full border-blue-500/40 bg-gradient-to-r from-cyan-500/10 via-blue-500/10 to-purple-600/10 text-blue-400 hover:border-purple-500/60 hover:from-cyan-500/20 hover:via-blue-500/20 hover:to-purple-600/20 hover:text-purple-300 transition-all duration-300"
                 onClick={() => {
                   setIsForgotPassword(false);
                   setResetEmailSent(false);
                   setEmail("");
                 }}
               >
                 Zurück zum Login
               </Button>
             </div>
           ) : (
             <div className="py-2">
               <h2 className="text-xl font-bold text-center mb-2 text-foreground">
                 Passwort vergessen?
               </h2>
               <p className="text-muted-foreground text-center mb-6 text-sm">
                 Geben Sie Ihre E-Mail-Adresse ein und wir senden Ihnen einen Link zum Zurücksetzen.
               </p>
 
               <form onSubmit={handleForgotPassword} className="space-y-4">
                 <div className="space-y-2">
                   <Label htmlFor="email-forgot" className="text-foreground">E-Mail</Label>
                   <Input
                     id="email-forgot"
                     type="email"
                     placeholder="ihre@email.de"
                     value={email}
                     onChange={(e) => setEmail(e.target.value)}
                     required
                     disabled={isLoading}
                     className="bg-black/50 border-blue-500/30 focus:border-cyan-500/50 text-foreground placeholder:text-muted-foreground"
                   />
                 </div>
 
                 <Button 
                   type="submit" 
                   className="w-full bg-gradient-to-r from-cyan-500 via-blue-500 to-purple-600 hover:from-cyan-400 hover:via-blue-400 hover:to-purple-500 text-white border-0 transition-all duration-300" 
                   disabled={isLoading}
                 >
                   {isLoading ? (
                     <>
                       <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                       Senden...
                     </>
                   ) : (
                     <>
                       <Mail className="mr-2 h-4 w-4" />
                       Reset-Link senden
                     </>
                   )}
                 </Button>
               </form>
 
               <div className="mt-4 text-center">
                 <button
                   type="button"
                   onClick={() => setIsForgotPassword(false)}
                   className="text-sm text-muted-foreground hover:text-cyan-400 transition-colors"
                 >
                   Zurück zum Login
                 </button>
               </div>
             </div>
           )
         ) : (
           <div className="py-2">
             <h2 className="text-xl font-bold text-center mb-2 text-foreground">
               {isSignUp ? "Konto erstellen" : "Willkommen zurück"}
             </h2>
             <p className="text-muted-foreground text-center mb-6 text-sm">
               {isSignUp 
                 ? "Registrieren Sie sich für den internen Bereich" 
                 : "Melden Sie sich im internen Bereich an"}
             </p>
 
             <form onSubmit={handleSubmit} className="space-y-4">
               <div className="space-y-2">
                 <Label htmlFor="email-login" className="text-foreground">E-Mail</Label>
                 <Input
                   id="email-login"
                   type="email"
                   placeholder="ihre@email.de"
                   value={email}
                   onChange={(e) => setEmail(e.target.value)}
                   required
                   disabled={isLoading}
                   className="bg-black/50 border-blue-500/30 focus:border-cyan-500/50 text-foreground placeholder:text-muted-foreground"
                 />
               </div>
 
               <div className="space-y-2">
                 <div className="flex items-center justify-between">
                   <Label htmlFor="password-login" className="text-foreground">Passwort</Label>
                   {!isSignUp && (
                     <button
                       type="button"
                       onClick={() => setIsForgotPassword(true)}
                       className="text-xs text-cyan-400 hover:text-purple-400 transition-colors"
                     >
                       Passwort vergessen?
                     </button>
                   )}
                 </div>
                 <Input
                   id="password-login"
                   type="password"
                   placeholder="••••••••"
                   value={password}
                   onChange={(e) => setPassword(e.target.value)}
                   required
                   minLength={6}
                   disabled={isLoading}
                   className="bg-black/50 border-blue-500/30 focus:border-cyan-500/50 text-foreground placeholder:text-muted-foreground"
                 />
               </div>
 
               <Button 
                 type="submit" 
                 className="w-full bg-gradient-to-r from-cyan-500 via-blue-500 to-purple-600 hover:from-cyan-400 hover:via-blue-400 hover:to-purple-500 text-white border-0 transition-all duration-300" 
                 disabled={isLoading}
               >
                 {isLoading ? (
                   <>
                     <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                     {isSignUp ? "Registrieren..." : "Anmelden..."}
                   </>
                 ) : (
                   <>
                     <Lock className="mr-2 h-4 w-4" />
                     {isSignUp ? "Registrieren" : "Anmelden"}
                   </>
                 )}
               </Button>
             </form>
 
             <div className="mt-4 text-center">
               <button
                 type="button"
                 onClick={() => setIsSignUp(!isSignUp)}
                 className="text-sm text-muted-foreground hover:text-cyan-400 transition-colors"
               >
                 {isSignUp 
                   ? "Bereits ein Konto? Jetzt anmelden" 
                   : "Noch kein Konto? Jetzt registrieren"}
               </button>
             </div>
 
             <p className="text-xs text-muted-foreground text-center mt-4">
               Der interne Bereich ist nur für autorisierte Teammitglieder zugänglich.
             </p>
           </div>
         )}
       </DialogContent>
     </Dialog>
   );
 };